import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation, NavLink, Link } from 'react-router-dom';
import {
   Box,
   Flex,
   Avatar,
   Image,
   Stack,
   Drawer,
   DrawerBody,
   DrawerHeader,
   DrawerOverlay,
   DrawerContent,
   DrawerCloseButton,
   useDisclosure,
   IconButton,
   Text,
   Menu,
   MenuButton,
   MenuList,
   MenuItem,
   HStack,
   useColorModeValue,
   Tooltip,
   Divider,
} from '@chakra-ui/react';
import { TbDotsVertical } from 'react-icons/tb';
import { MdEdit } from 'react-icons/md';
import { FaRegEye, FaTrash } from 'react-icons/fa';
import { IoIosLogOut, IoMdSettings } from 'react-icons/io';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import PlusIcon from 'assets/icons/PlusIcon';
import { HSeparator } from 'components/separator/Separator';

import { deleteCompany } from '../../redux/actions/company';
import { userProfile, updateUserHistory, ChangeCompany } from '../../redux/actions/user';
import IconLogo from 'assets/img/zeroninelogoicon.png';
import CompanyWithGst from 'views/admin/companyProfile/CompanyWithGst';
import CompanySettingForm from 'views/admin/companyProfile/CompanySettingForm';
import CompanyForm from 'views/admin/companyProfile/CompanyForm';
import DeleteDialog from 'views/admin/masters/components/DeleteDialog';
import { SearchBarGlobal } from 'components/navbar/searchBar/SearchBarGlobal';
import { logout } from 'utils/auth';
import { renderThumb, renderTrack, renderView } from 'components/scrollbar/Scrollbar';
import Scrollbars from 'react-custom-scrollbars-2';
import { useGlobal, useUser } from '../../contexts';
import { FaShareFromSquare } from 'react-icons/fa6';
import { authLogin } from '../../redux/actions/authentication';

function IconSidebar(props) {
   const dispatch = useDispatch();
   const navigate = useNavigate();
   const location = useLocation();
   const { companyList, company } = useGlobal();
   const { user } = useUser();
   const companyId = user?.loginhistory?.companyId;
   const companyRight = Array.isArray(user?.userRights)
      ? user.userRights.find((right) => right.companyId === companyId)?.companyprofile
      : null;

   // Color mode values
   let activeColor = useColorModeValue('#2C7865', 'gray.700');
   let inactiveColor = useColorModeValue('secondaryGray.600', 'secondaryGray.600');
   let activeIcon = useColorModeValue('#2C7865', 'gray.700');
   let textColor = useColorModeValue('#686D76', 'white');
   let hoverBg = useColorModeValue('#F0FFF0', 'gray.500');
   let separator = useColorModeValue('gray.300', 'gray.700');
   let variantChange = '0.2s linear';
   let sidebarBg = useColorModeValue('white', 'navy.800');

   let selectedSharedComp;
   const [selectedCompanyId, setSelectedCompanyId] = useState(null);
   const {
      isOpen: isOpenShareCompanySetting,
      onOpen: onOpenShareCompanySetting,
      onClose: onCloseShareCompanySetting,
   } = useDisclosure();
   const [isLoading, setIsLoading] = useState(false);
   const [compShare, setCompShare] = useState(false);
   const [activeCompany, setActiveCompany] = useState(null);

   // Drawer states
   const { isOpen, onOpen, onClose } = useDisclosure();
   const { isOpen: isOpenCompanyModal, onOpen: onOpenCompanyModal, onClose: onCloseCompanyModal } = useDisclosure();
   const { isOpen: isOpenCompany, onOpen: onOpenCompany, onClose: onCloseCompany } = useDisclosure();
   const {
      isOpen: isOpenCompanySetting,
      onOpen: onOpenCompanySetting,
      onClose: onCloseCompanySetting,
   } = useDisclosure();

   // Submenu state
   const [openSubmenus, setOpenSubmenus] = useState({});

   // Component state
   const [settingData, setSettingData] = useState(null);
   const [companyData, setCompanyData] = useState(null);
   const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
   const [selectedId, setSelectedId] = useState(null);

   const btnRef = React.useRef();

   // Auto-open parent menus when their children are active
   useEffect(() => {
      const newOpenStates = {};
      props.routes.forEach((route) => {
         if (route.items) {
            const hasActiveChild = route.items.some((child) => {
               if (child.items) {
                  return child.items.some((subChild) => activeRoute(subChild));
               }
               return activeRoute(child);
            });

            if (hasActiveChild) {
               newOpenStates[route.name] = true;
            }
         }
      });
      setOpenSubmenus((prev) => ({ ...prev, ...newOpenStates }));
   }, [location.pathname]);

   const toggleSubmenu = (name, route) => {
      setOpenSubmenus((prev) => {
         const newState = { ...prev };

         // Close all other submenus
         Object.keys(newState).forEach((key) => {
            if (key !== name) newState[key] = false;
         });

         // Toggle current submenu
         const isOpening = !prev[name];
         newState[name] = isOpening;

         // If opening and has default child, navigate to it
         if (isOpening && route.items) {
            const defaultChild = route.items.find((child) => child.isDefault);
            if (defaultChild && !activeRoute(route)) {
               navigate(route.layout + defaultChild.path);
            }
         }

         return newState;
      });
   };

   // Check if any child route is active
   const hasActiveChild = (route) => {
      if (!route.items) return false;
      return route.items.some((child) => {
         if (child.items) return hasActiveChild(child);
         return activeRoute(child);
      });
   };

   const activeRoute = (route) => {
      const currentPath = location.pathname;

      if (route.parentFor) {
         return route.parentFor.some((parentPath) => currentPath.includes(parentPath));
      }

      if (route.path) {
         if (route.exactMatch) {
            return currentPath === route.layout + route.path;
         }
         if (route.activeSegment) {
            return currentPath.includes(route.activeSegment);
         }
         return currentPath.includes(route.path);
      }

      return false;
   };

   // Create sidebar links with submenu support
   const createLinks = (routes, isSubmenu = false) => {
      return routes?.map((route, index) => {
         if (route.type === 'separator') {
            return <Box key={`separator-${index}`} height="2px" width="100%" my="5px" bg={separator} />;
         }

         if (route.category) {
            return (
               <React.Fragment key={index}>
                  <Text
                     fontSize={'md'}
                     color={activeColor}
                     fontWeight="bold"
                     mx="auto"
                     ps={{ sm: '10px', xl: '16px' }}
                     pt="18px"
                     pb="12px">
                     {route.name}
                  </Text>
                  {createLinks(route.items)}
               </React.Fragment>
            );
         } else if (route.items) {
            const isParentActive = hasActiveChild(route);
            const isSubmenuOpen = openSubmenus[route.name];

            // Only show active background when dropdown is closed but has active child
            const shouldShowActiveBg = isParentActive && !isSubmenuOpen;

            return (
               <Box key={index}>
                  <Box
                     onClick={() => toggleSubmenu(route.name, route)}
                     py="5px"
                     px="10px"
                     display="flex"
                     p={2}
                     alignItems="center"
                     justifyContent="space-between"
                     borderRadius="full"
                     bg={shouldShowActiveBg ? '#E0FBE2' : 'transparent'}
                     _hover={{
                        bg: shouldShowActiveBg ? '#E0FBE2' : hoverBg,
                        cursor: 'pointer',
                     }}>
                     <Flex flexDirection="column" alignItems="center">
                        <Box color={shouldShowActiveBg ? activeIcon : textColor} w={''}>
                           {route.icon}
                        </Box>
                        <Text
                           color={shouldShowActiveBg ? activeColor : textColor}
                           fontWeight={shouldShowActiveBg ? 'bold' : 'normal'}
                           fontSize={11}
                           textAlign="center"
                           lineHeight="15px">
                           {route.name}
                        </Text>
                     </Flex>

                     {route.items && (
                        <Box>{isSubmenuOpen ? <FiChevronDown size="15px" /> : <FiChevronRight size="15px" />}</Box>
                     )}
                  </Box>

                  {isSubmenuOpen && (
                     <Box fontSize={'15px'} textAlign={'center'}>
                        {createLinks(route.items, true)}
                     </Box>
                  )}
               </Box>
            );
         } else if (route.layout === '/admin' || route.layout === '/auth' || route.layout === '/rtl') {
            return (
               <NavLink key={index} to={route.layout + route.path}>
                  {route.icon ? (
                     <Box
                        bg={activeRoute(route) ? '#E0FBE2' : 'transparent'}
                        py="5px"
                        px="5px"
                        borderRadius="10px"
                        _hover={{
                           bg: activeRoute(route) ? '#E0FBE2' : hoverBg,
                           cursor: 'pointer',
                        }}>
                        <Tooltip label={route.shortCut} hasArrow placement="right">
                           <Flex flexDirection="column" alignItems="center">
                              <Box color={activeRoute(route) ? activeIcon : textColor} w={''}>
                                 {route.icon}
                              </Box>
                              <Text
                                 color={activeRoute(route) ? activeColor : textColor}
                                 fontWeight={activeRoute(route) ? 'bold' : 'normal'}
                                 fontSize={11}
                                 textAlign="center"
                                 lineHeight="15px">
                                 {route.name}
                              </Text>
                           </Flex>
                        </Tooltip>
                     </Box>
                  ) : (
                     <Box mx="auto">
                        <HStack spacing={activeRoute(route) ? '22px' : '26px'} py="5px" ps="10px">
                           <Text
                              me="auto"
                              color={activeRoute(route) ? activeColor : inactiveColor}
                              fontWeight={activeRoute(route) ? 'bold' : 'normal'}>
                              {route.name}
                           </Text>
                           <Box h="36px" w="4px" bg="brand.400" borderRadius="5px" />
                        </HStack>
                     </Box>
                  )}
               </NavLink>
            );
         }
         return null;
      });
   };

   useEffect(() => {
      // Set initial selected company from user data or localStorage
      if (user?.loginhistory?.companyId) {
         setSelectedCompanyId(user.loginhistory.companyId);
      } else if (localStorage.getItem('selectedCompanyId')) {
         setSelectedCompanyId(localStorage.getItem('selectedCompanyId'));
      } else if (companyList && companyList.length > 0) {
         setSelectedCompanyId(companyList[0].id);
         // Save to localStorage
         localStorage.setItem('selectedCompanyId', companyList[0].id);
      }
   }, [user, companyList]);

   const handleOpenShareCompanySetting = (id) => {
      selectedSharedComp = companyList?.find((company) => company.id.toString() === id.toString()) ?? null;
      setSettingData(selectedSharedComp);
      onOpenShareCompanySetting();
   };

   const currentCompany = companyList.find((c) =>
      user?.loginhistory?.code && user?.loginhistory?.code !== ''
         ? c.id === user?.loginhistory?.companyId && user?.loginhistory?.code === c?.dbName
         : c.id === user?.loginhistory?.companyId,
   );

   const handleSharedSwitchCompany = async (company) => {
      try {
         // localStorage.removeItem('authTokenCustomer');
         setIsLoading(true);
         setActiveCompany(company);
         setSelectedCompanyId(company.id);
         localStorage.setItem('selectedCompanyId', company.id);
         localStorage.setItem('dbName', company.dbName);

         const loginData = {
            code: company.dbName,
         };

         await dispatch(ChangeCompany(loginData));
         window.location.reload();
         await dispatch(updateUserHistory({ companyId: company.id, code: company.dbName }));
         setCompShare(true);
         await dispatch(userProfile());
         onClose();
         navigate('/admin/default');
      } catch (error) {
         console.error('Error switching to shared company:', error);
      } finally {
         setIsLoading(false);
      }
   };

   const handleSwitchCompany = async (company, lastType) => {
      try {
         setIsLoading(true);
         setActiveCompany(company);
         setSelectedCompanyId(company.id);
         // localStorage.setItem('selectedCompanyId', company.id);
         // localStorage.setItem('dbName', company.dbName);

         if (currentCompany && currentCompany?.dbName && user?.loginhistory?.code === currentCompany?.dbName) {
            // localStorage.removeItem('authTokenCustomer');
            await dispatch(ChangeCompany());
            setCompShare(false);
            window.location.reload();
         }
         // if (compShare) {
         //    console.log('company', company, compShare);
         //    localStorage.removeItem('authTokenCustomer');
         //    const loginData = {
         //       email: localStorage.getItem('email'),
         //       password: localStorage.getItem('password'),
         //    };
         //    await dispatch(authLogin(navigate, loginData));
         //    setCompShare(false);
         // }
         await dispatch(updateUserHistory({ companyId: company.id, code: '' }));
         await dispatch(userProfile());
         onClose();
         navigate('/admin/default');
      } catch (error) {
         console.error('Error switching company:', error);
      } finally {
         setIsLoading(false);
      }
   };

   const handleOpenCompany = () => {
      onClose();
      onOpenCompany();
   };

   const handleOpenCompanySetting = (id) => {
      const company = companyList?.find((company) => company.id.toString() === id.toString()) ?? null;
      setSettingData(company);
      onOpenCompanySetting();
   };

   const handleOpenCompanyModal = useCallback(
      (id) => {
         const company = companyList?.find((company) => company.id.toString() === id.toString()) ?? null;
         setCompanyData(company);
         onOpenCompanyModal();
      },
      [companyList, onOpenCompanyModal],
   );

   const handleOnDelete = useCallback(
      (id) => {
         setSelectedId(id);
         setDeleteDialogOpen(true);
         onClose();
      },
      [onClose],
   );

   const handleOnShow = useCallback(
      (id) => {
         navigate(`/admin/co-profile`, { replace: true });
         onClose();
      },
      [navigate, onClose],
   );

   const handleDelete = () => {
      dispatch(deleteCompany(selectedId));
      setDeleteDialogOpen(false);
      window.location.reload();
   };

   return (
      <>
         {/* Main Sidebar */}
         <Box display={{ base: 'none', lg: 'block' }} position="fixed" top="0" left="0" minH="100%">
            <Box bg={sidebarBg} transition={variantChange} w="100px" h="100vh" minH="100%" overflowX="hidden">
               <Scrollbars
                  autoHide
                  renderTrackVertical={renderTrack}
                  renderThumbVertical={renderThumb}
                  renderView={renderView}>
                  <Flex direction="column" height="100%" pt="3px" px="16px" borderRadius="30px" mx={'auto'}>
                     <Image src={IconLogo} alt="Logo" w="70%" mx={'auto'} mb={2} />
                     <Box
                        bg="blue.100"
                        p={2}
                        rounded="full"
                        onClick={onOpen}
                        cursor="pointer"
                        w="50px"
                        h="50px"
                        mx="auto">
                        <Flex>
                           {company?.logo ? (
                              <img
                                 src={company?.liveUrl}
                                 crossOrigin="anonymous"
                                 alt="Logo"
                                 className="w-8 h-8 object-cover rounded-full ring-2 ring-offset-2 ring-emerald-400/30"
                              />
                           ) : (
                              <Avatar
                                 name={company?.name}
                                 src="https://bit.ly/broken-link"
                                 colorPalette="teal"
                                 size="sm"
                                 className="ring-2 ring-offset-2 ring-emerald-400/30"
                              />
                           )}
                        </Flex>
                     </Box>
                     <Stack direction="column" mb="auto" mt="8px">
                        <Box ps="0px" pe={{ md: '16px', '2xl': '1px' }} mx={'auto'}>
                           {/* Render navigation links with submenu support */}
                           {createLinks(props.routes)}
                           <Box mx="auto" position="relative" top="8%">
                              <HStack
                                 spacing={'26px'}
                                 py="5px"
                                 ps="10px"
                                 onClick={() => {
                                    logout();
                                    navigate('/auth/sign-in');
                                    window.location.reload();
                                 }}
                                 as="button"
                                 bgGradient="linear(to-r, brand.400, purple.500)"
                                 _hover={{
                                    bgGradient: 'linear(to-r, brand.500, purple.600)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: 'md',
                                 }}
                                 _active={{
                                    bgGradient: 'linear(to-r, brand.600, purple.700)',
                                    transform: 'translateY(0)',
                                 }}
                                 borderRadius="md"
                                 px="4"
                                 transition="all 0.2s"
                                 w="100%">
                                 <Box mx="auto">
                                    {' '}
                                    <IoIosLogOut color="white" size={22} />{' '}
                                 </Box>
                              </HStack>
                           </Box>
                        </Box>
                     </Stack>
                  </Flex>
               </Scrollbars>
            </Box>
         </Box>

         {/* Company Switcher Drawer */}
         <Drawer isOpen={isOpen} placement="left" onClose={onClose} finalFocusRef={btnRef}>
            <DrawerOverlay />
            <DrawerContent>
               <DrawerCloseButton />
               <DrawerHeader>Switch Company</DrawerHeader>
               <HSeparator />
               <DrawerBody>
                  <Flex justifyContent="space-between" alignItems="center">
                     <Box onClick={() => handleOpenCompany()} cursor="pointer">
                        <Text fontWeight="medium" textTransform="uppercase">
                           My Company
                        </Text>
                     </Box>
                     <Box>
                        {user?.isAdmin && (
                           <IconButton
                              variant="ghost"
                              ref={btnRef}
                              onClick={() => handleOpenCompany()}
                              icon={<PlusIcon />}
                           />
                        )}
                     </Box>
                  </Flex>
                  <Box>
                     {companyList
                        ?.filter((x) => !x.isShared)
                        ?.map((company) => {
                           const storedSelectedCompanyId = localStorage.getItem('selectedCompanyId');
                           const storedCompanyType = localStorage.getItem('selectedCompanyType');

                           const isSelected =
                              (company.id === storedSelectedCompanyId && storedCompanyType === 'regular') ||
                              (company.id === user?.loginhistory?.companyId &&
                                 (!storedCompanyType || storedCompanyType === 'regular')) ||
                              (companyList?.length === 1 && company.id === companyList[0].id);

                           return (
                              <Box
                                 my={3}
                                 p={4}
                                 bg={isSelected ? 'blue.100' : 'white'}
                                 key={company.id}
                                 cursor="pointer"
                                 borderRadius="20px">
                                 <Flex justifyContent="space-between" alignItems="center">
                                    <Box
                                       onClick={() => {
                                          handleSwitchCompany(company);
                                          localStorage.setItem('selectedCompanyId', company.id);
                                          localStorage.setItem('selectedCompanyType', 'regular');
                                       }}>
                                       <Flex justifyContent="start" alignItems="center" gap={2}>
                                          <Box w={8} h={8}>
                                             {company?.logo ? (
                                                <img
                                                   src={company?.liveUrl}
                                                   crossOrigin="anonymous"
                                                   alt="Logo"
                                                   className="w-full h-full object-cover rounded-full"
                                                />
                                             ) : (
                                                <Avatar
                                                   name={company.name}
                                                   src="https://bit.ly/broken-link"
                                                   colorPalette="teal"
                                                   size="sm"
                                                />
                                             )}
                                          </Box>
                                          <Box>
                                             <Text textStyle="4xl" fontWeight="semibold" color="#000000">
                                                {company.name}
                                             </Text>
                                          </Box>
                                       </Flex>
                                    </Box>
                                    <Box>
                                       <Menu>
                                          <MenuButton
                                             as={IconButton}
                                             aria-label="Options"
                                             icon={<TbDotsVertical />}
                                             variant="ghost"
                                             color="#000000"
                                          />
                                          <MenuList>
                                             {(user?.isAdmin || companyRight?.edit) && (
                                                <MenuItem
                                                   icon={<MdEdit />}
                                                   onClick={() => handleOpenCompanyModal(company.id)}>
                                                   Edit
                                                </MenuItem>
                                             )}
                                             <Link to={`/admin/setting/co-profile`}>
                                                <MenuItem icon={<FaRegEye />} onClick={() => handleOnShow(company.id)}>
                                                   Show Details
                                                </MenuItem>
                                             </Link>
                                             {user?.packageData?.[0]?.share_company && user?.isAdmin && (
                                                <MenuItem
                                                   icon={<FaShareFromSquare />}
                                                   onClick={() => handleOpenShareCompanySetting(company.id)}>
                                                   Share Company
                                                </MenuItem>
                                             )}
                                             {(user?.isAdmin || companyRight?.edit) && (
                                                <MenuItem
                                                   icon={<IoMdSettings />}
                                                   onClick={() => handleOpenCompanySetting(company.id)}>
                                                   Setting
                                                </MenuItem>
                                             )}
                                             {user?.isAdmin && (
                                                <MenuItem icon={<FaTrash />} onClick={() => handleOnDelete(company.id)}>
                                                   Delete
                                                </MenuItem>
                                             )}
                                          </MenuList>
                                       </Menu>
                                    </Box>
                                 </Flex>
                              </Box>
                           );
                        })}
                  </Box>
                  <Divider />
                  <Flex justifyContent="space-between" alignItems="center">
                     <Box onClick={() => handleOpenCompany()} cursor="pointer">
                        <Text fontWeight="medium" textTransform="uppercase">
                           Shared Company
                        </Text>
                     </Box>
                  </Flex>
                  <Box>
                     {companyList
                        ?.filter((x) => x.isShared)
                        ?.map((company) => {
                           const storedSelectedCompanyId = localStorage.getItem('selectedCompanyId');
                           const storedCompanyType = localStorage.getItem('selectedCompanyType');

                           const isSelected =
                              (company.id === storedSelectedCompanyId && storedCompanyType === 'shared') ||
                              (company.id === user?.loginhistory?.companyId && storedCompanyType === 'shared');

                           return (
                              <Box
                                 my={3}
                                 p={4}
                                 bg={isSelected ? 'blue.100' : 'white'}
                                 key={company.id}
                                 cursor="pointer"
                                 borderRadius="20px">
                                 <Flex justifyContent="space-between" alignItems="center">
                                    <Box
                                       onClick={() => {
                                          handleSharedSwitchCompany(company);
                                          localStorage.setItem('selectedCompanyId', company.id);
                                          localStorage.setItem('selectedCompanyType', 'shared');
                                       }}>
                                       <Flex justifyContent="start" alignItems="center" gap={2}>
                                          <Box>
                                             <Avatar
                                                name={company.name}
                                                src="https://bit.ly/broken-link"
                                                colorPalette="teal"
                                                size="sm"
                                             />
                                          </Box>
                                          <Box>
                                             <Text textStyle="4xl" fontWeight="semibold" color={textColor}>
                                                {company.name}
                                             </Text>
                                          </Box>
                                       </Flex>
                                    </Box>
                                    <Box>
                                       <Menu>
                                          <MenuButton
                                             as={IconButton}
                                             aria-label="Options"
                                             icon={<TbDotsVertical />}
                                             variant="ghost"
                                             color={textColor}
                                          />
                                          <MenuList>
                                             {/* <MenuItem
                                                icon={<MdEdit />}
                                                onClick={() => handleOpenCompanyModal(company.id)}>
                                                Edit
                                             </MenuItem> */}
                                             <Link to={`/admin/setting/co-profile`}>
                                                <MenuItem icon={<FaRegEye />} onClick={() => handleOnShow(company.id)}>
                                                   Show Details
                                                </MenuItem>
                                             </Link>
                                             {/* <MenuItem
                                                icon={<IoMdSettings />}
                                                onClick={() => handleOpenCompanySetting(company.id)}>
                                                Setting
                                             </MenuItem> */}
                                             {/* <MenuItem icon={<FaTrash />} onClick={() => handleOnDelete(company.id)}>
                                                Delete
                                             </MenuItem> */}
                                          </MenuList>
                                       </Menu>
                                    </Box>
                                 </Flex>
                              </Box>
                           );
                        })}
                  </Box>
               </DrawerBody>
            </DrawerContent>
         </Drawer>

         {/* Modals */}
         <CompanyWithGst isOpen={isOpenCompany} onClose={onCloseCompany} />
         <CompanySettingForm
            isOpen={isOpenCompanySetting}
            onClose={onCloseCompanySetting}
            isEditMode={true}
            currentRow={settingData}
         />
         <CompanyForm
            isOpen={isOpenCompanyModal}
            onClose={onCloseCompanyModal}
            isEditMode={true}
            currentRow={companyData && companyData?.id ? companyData : null}
         />
         <DeleteDialog
            isDeleteDialogOpen={isDeleteDialogOpen}
            setDeleteDialogOpen={setDeleteDialogOpen}
            handleDelete={handleDelete}
            title="Company"
         />
      </>
   );
}

export default IconSidebar;
